import type { Work, WorkProviderRef } from "@/types/work";

/**
 * 作品IDの扱いを1か所にまとめる。
 *
 * - サイト内ID（Work.id）… 保存・URL・お気に入りに使う唯一のキー
 * - 提供元ID（providerKey / serviceId / providerId）… 外部APIとの突き合わせ用
 * - 参照キー … 同じ providerId が別サービスにあっても衝突しない複合キー
 */

/** 提供元キーの既定値（デモデータはサイト自身が提供元） */
export const DEMO_PROVIDER_KEY = "yumesearch";

/** デモ作品の providerId 接頭辞（サイト内IDとは別の値を示すため） */
export const DEMO_PROVIDER_ID_PREFIX = "DMY";

const SEPARATOR = "\u0000";

/** 外部商品を一意に参照するための複合キー */
export function buildProviderRefKey(ref: WorkProviderRef): string {
  return [ref.providerKey.trim().toLowerCase(), (ref.serviceId ?? "").trim().toLowerCase(), ref.providerId.trim()]
    .join(SEPARATOR);
}

export function getWorkRefKey(work: Pick<Work, "providerKey" | "providerId" | "serviceId">): string {
  return buildProviderRefKey({
    providerKey: work.providerKey,
    providerId: work.providerId,
    serviceId: work.serviceId,
    floorId: null,
  });
}

/** デモ作品用の提供元ID（内部IDから決定的に作る。既存IDは変更しない） */
export function buildDemoProviderId(internalId: string): string {
  return `${DEMO_PROVIDER_ID_PREFIX}-${internalId}`;
}

/**
 * 作品詳細などのURLに使う内部IDを安全にエンコードする。
 * 外部IDは数字だけに固定しないため、記号やスラッシュを含む可能性がある。
 */
export function encodeWorkId(id: string): string {
  return encodeURIComponent(id);
}

export function decodeWorkId(value: string | undefined): string {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    // React Router は既にデコード済みの値を渡すことがある
    return value;
  }
}

/** 作品詳細ページのパス（URLに出すIDは必ずエンコードする） */
export function workPath(id: string): string {
  return `/works/${encodeWorkId(id)}`;
}